import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { LoginUser, SubscriptionAccess } from '../types/auth';
import { PlatformAddOn } from '../types/subscription';

export interface AuthClaims {
  uid: string;
  brandingId: string;
  roles: string[];

  impersonated?: string;
  impersonatedBy?: string;

  exp?: number;
}

interface AuthContextType {
  token: string | null;
  claims: AuthClaims | null;
  user: LoginUser | null;
  subscription: SubscriptionAccess | null;
  isAdmin: boolean;

  hasAddOn: (addOn: PlatformAddOn) => boolean;

  login: (
    token: string,
    user: LoginUser,
    subscription: SubscriptionAccess | null
  ) => void;

  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getStoredUser = (): LoginUser | null => {
  try {
    const value = localStorage.getItem('user');

    return value
      ? JSON.parse(value)
      : null;
  } catch {
    return null;
  }
};

const getStoredSubscription = (): SubscriptionAccess | null => {
  try {
    const value = localStorage.getItem('subscription');

    return value
      ? JSON.parse(value)
      : null;
  } catch {
    return null;
  }
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(
    localStorage.getItem('token')
  );

  const [user, setUser] = useState<LoginUser | null>(
    getStoredUser
  );

  const [subscription, setSubscription] =
    useState<SubscriptionAccess | null>(
      getStoredSubscription
    );

  const navigate = useNavigate();

  const claims = useMemo((): AuthClaims | null => {
    if (!token) return null;

    try {
      const payload = jwtDecode<Record<string, unknown>>(token);
      const rawRoles =
        payload.roles ??
        payload.role ??
        payload[
          'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
        ] ??
        [];
      const roles = Array.isArray(rawRoles)
        ? rawRoles
        : [rawRoles];

      return {
        uid: String(payload.uid ?? ''),
        brandingId: String(payload.brandingId ?? ''),
        roles: roles.map(String),
        impersonated: String(payload.impersonated ?? ''),
        impersonatedBy: String(payload.impersonatedBy ?? ''),
        exp: typeof payload.exp === 'number'
          ? payload.exp
          : undefined,
      };
    } catch {
      return null;
    }
  }, [token]);

  const clearSession = useCallback(
    (destination = '/login') => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('subscription');

      setToken(null);
      setUser(null);
      setSubscription(null);

      navigate(destination, {
        replace: true,
      });
    },
    [navigate]
  );

  const login = useCallback((newToken: string,
    newUser: LoginUser, newSubscription: SubscriptionAccess | null) => {
    localStorage.setItem(
      'token',
      newToken
    );

    localStorage.setItem(
      'user',
      JSON.stringify(newUser)
    );

    if (newSubscription) {
      localStorage.setItem(
        'subscription',
        JSON.stringify(newSubscription)
      );
    } else {
      localStorage.removeItem(
        'subscription'
      );
    }

      setToken(newToken);
      setUser(newUser);
      setSubscription(newSubscription);
    },
    []
  );

  const logout = useCallback(async () => {
    clearSession();
  }, [clearSession]);

  useEffect(() => {
    if (token && (!claims || (claims.exp && claims.exp * 1000 <= Date.now()))) {
      clearSession();
    }
  }, [claims, clearSession, token]);

  useEffect(() => {
    const handleUnauthorized = () => clearSession();
    const handlePasswordExpired = () => clearSession('/password-expired');
    window.addEventListener('strivre:unauthorized', handleUnauthorized);
    window.addEventListener('strivre:password-expired', handlePasswordExpired);
    return () => {
      window.removeEventListener('strivre:unauthorized', handleUnauthorized);
      window.removeEventListener('strivre:password-expired', handlePasswordExpired);
    };
  }, [clearSession]);

  const isAdmin = claims?.roles.some((role) => role.toLowerCase() === 'admin') ?? false;
  const hasAddOn = useCallback(
    (addOn: PlatformAddOn) => {
      if (isAdmin) {
        return true;
      }

      return (
        subscription?.addOns?.includes(addOn) ??
        false
      );
    },
    [isAdmin, subscription]
  );

  return (
    <AuthContext.Provider
      value={{
        token,
        claims,
        user,
        subscription,
        isAdmin,
        hasAddOn,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};